import React from 'react';
import styled from 'styled-components';

const PrivacySectionContainer = styled.div`
  padding-bottom: 10px;
  margin-left: 100px;
  margin-right: 100px;
  font-family: 'Inter';
  color: #101010;

  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 90%;
    margin: 0 auto;
    box-sizing: border-box;
    padding: 10px;
  }
`;

const MainHeading = styled.h1`
  font-family: 'Oswald';
  font-size: 60px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  text-align: left;

  @media screen and (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 15px;
    line-height: 1.2;
  }
`;

const SubHeading = styled.h2`
  font-family: 'Oswald';
  font-size: 25px;
  font-weight: 500;
  color: #333;
  margin-bottom: 10px;
  text-align: left;

  @media screen and (max-width: 768px) {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 15px;
  }
`;

const SubSection = styled.h3`
  font-family: 'Inter';
  font-size: 14px;
  font-weight: 200;
  margin-left: 20px;
  font-size: 18px;
  font-weight: 600;
  color: #666;
  margin-bottom: 10px;
  text-align: left;

  @media screen and (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 10px;
  }
`;

const SubSubSection = styled.h4`
  font-family: 'Inter';
  font-size: 14px;
  font-weight: 200;
  margin-left: 40px;
`;

const InfoDiv = styled.div`
  font-family: 'Inter';
  font-size: 16px;
  font-weight: 200;
  color: #444;
  text-align: left;
  padding-bottom: 20px;

  @media screen and (max-width: 768px) {
    font-size: 14px;
    line-height: 1.5;
    padding-bottom: 15px;
  }

  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  strong {
    font-weight: 600;
  }
`;

const MainInfoDiv = styled.div`
  font-family: 'Inter';
  font-size: 20px;
  font-weight: 600;
  color: #444;
  line-height: 0.95;
  text-align: left;

  @media screen and (max-width: 768px) {
    font-size: 18px;
    line-height: 1.5;
    margin-bottom: 20px;
  }

  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  strong {
    font-weight: 600;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
  margin: 20px 0;

  @media screen and (max-width: 768px) {
    padding: 10px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  font-size: 16px;
  text-align: left;

  @media screen and (max-width: 768px) {
    font-size: 14px;
    margin: 10px 0;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 12px 15px;

    @media screen and (max-width: 768px) {
      padding: 8px 10px;
    }
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
    color: #333;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #f1f1f1;
  }

  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const PrivacySection = ({
  heading,
  subHeading,
  info,
  mainHeading,
  mainInfo,
  tableData,
}) => {
  const processText = (text, boldFirstWord) => {
    if (typeof text !== 'string') return text;

    if (boldFirstWord) {
      const words = text.split(' ');
      if (words.length > 0) {
        words[0] = `<strong>${words[0]}</strong>`;
      }
      text = words.join(' ');
    }

    const linkRegex = /\[([^\]]+)]\((https?:\/\/[^\s]+)\)/g;
    text = text.replace(linkRegex, (_, linkText, linkUrl) => {
      return `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    });

    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    text = text.replace(emailRegex, email => {
      return `<a href="mailto:${email}">${email}</a>`;
    });

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    text = text.replace(urlRegex, url => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });

    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  return (
    <PrivacySectionContainer>
      {mainHeading && (
        <>
          <MainHeading>{mainHeading}</MainHeading>
          <MainInfoDiv>{processText(mainInfo, false)}</MainInfoDiv>
        </>
      )}
      {heading && <SubHeading>{heading}</SubHeading>}
      {subHeading && <SubSection>{subHeading}</SubSection>}
      {info && Array.isArray(info) ? (
        info.map((paragraph, index) => (
          <InfoDiv key={index}>
            {typeof paragraph === 'string' ? (
              processText(paragraph, false)
            ) : (
              <>
                {paragraph.subHeading && (
                  <SubSubSection>{paragraph.subHeading}</SubSubSection>
                )}
                {Array.isArray(paragraph.content) ? (
                  paragraph.content.map((line, lineIndex) => (
                    <div key={lineIndex}>
                      {processText(line, paragraph.boldFirstWord)}
                    </div>
                  ))
                ) : (
                  <div>
                    {processText(paragraph.content, paragraph.boldFirstWord)}
                  </div>
                )}
              </>
            )}
          </InfoDiv>
        ))
      ) : (
        <InfoDiv>{processText(info, false)}</InfoDiv>
      )}
      {tableData && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                {tableData.headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{processText(cell, false)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </PrivacySectionContainer>
  );
};

export default PrivacySection;
